import { useEffect, useRef, useState } from "react";
import {
  useRefreshSenderConfig,
  getHealthCheckQueryKey,
  getGetDashboardSummaryQueryKey,
  type SenderConfig,
  type HealthStatus,
  type DashboardSummary,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  apiErrorMessage,
  apiErrorRateLimit,
  apiErrorStatus,
} from "@/lib/apiError";

/**
 * Mirror of the server-side `SENDER_RECHECK_LIMIT.windowMs` (see
 * `artifacts/api-server/src/lib/rateLimit.ts`). Used to keep the button
 * disabled with a visible countdown for the cooldown window after a
 * successful recheck so the next click won't immediately bounce off the
 * server's 429.
 */
const SENDER_RECHECK_COOLDOWN_MS = 10 * 1000;
const SENDER_RECHECK_COOLDOWN_SECONDS = SENDER_RECHECK_COOLDOWN_MS / 1000;

export type RecheckCooldownReason = "rate_limit" | "post_recheck";

export interface RecheckCooldownState {
  /** Wall-clock ms at which the cooldown ends. */
  activeUntilMs: number;
  /** Why the cooldown is in effect — drives banner messaging. */
  reason: RecheckCooldownReason;
  /** Seconds remaining until the cooldown ends. */
  secondsLeft: number;
}

interface RecheckSenderButtonProps {
  className?: string;
  variant?: "outline" | "ghost" | "secondary" | "default";
  size?: "default" | "sm";
  testId?: string;
  /**
   * Called whenever the cooldown state transitions or ticks down — either
   * entering a new cooldown (with `state` populated) or clearing one
   * (`null`). Lets the parent banner render an inline "try again in N
   * seconds" message when the user has hit the rate limit.
   */
  onCooldownChange?: (state: RecheckCooldownState | null) => void;
  /**
   * When true, hides the text label on mobile (< sm) so the button
   * collapses to icon-only, freeing toolbar space for siblings like the
   * Send button on cramped Compose-style headers. The full label still
   * appears at `sm` and above, and remains accessible via `aria-label`
   * and the cooldown tooltip on mobile.
   */
  compactOnMobile?: boolean;
}

function secondsUntil(timestampMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((timestampMs - nowMs) / 1000));
}

interface InternalCooldown {
  activeUntilMs: number;
  reason: RecheckCooldownReason;
}

export function RecheckSenderButton({
  className,
  variant = "outline",
  size = "sm",
  testId = "sender-recheck-button",
  onCooldownChange,
  compactOnMobile = false,
}: RecheckSenderButtonProps) {
  const queryClient = useQueryClient();
  const [cooldown, setCooldown] = useState<InternalCooldown | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const onCooldownChangeRef = useRef(onCooldownChange);
  onCooldownChangeRef.current = onCooldownChange;

  useEffect(() => {
    if (cooldown == null) return;
    if (cooldown.activeUntilMs <= Date.now()) {
      setCooldown(null);
      return;
    }
    setNow(Date.now());
    const id = window.setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= cooldown.activeUntilMs) {
        setCooldown(null);
        window.clearInterval(id);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [cooldown]);

  const secondsLeft =
    cooldown != null ? secondsUntil(cooldown.activeUntilMs, now) : 0;
  const isCountingDown = secondsLeft > 0;

  // Notify the parent on every cooldown transition *and* every tick so the
  // inline banner countdown stays in sync with the button's countdown.
  useEffect(() => {
    if (cooldown && isCountingDown) {
      onCooldownChangeRef.current?.({
        activeUntilMs: cooldown.activeUntilMs,
        reason: cooldown.reason,
        secondsLeft,
      });
    } else {
      onCooldownChangeRef.current?.(null);
    }
  }, [cooldown, isCountingDown, secondsLeft]);

  const refreshMutation = useRefreshSenderConfig({
    mutation: {
      onSuccess: (fresh: SenderConfig) => {
        const key = getHealthCheckQueryKey();
        const cached = queryClient.getQueryData<HealthStatus>(key);
        if (cached) {
          queryClient.setQueryData<HealthStatus>(key, {
            ...cached,
            senderConfig: fresh,
          });
        } else {
          queryClient.invalidateQueries({ queryKey: key });
        }

        // The Dashboard renders the same indicator from its own
        // `getDashboardSummary` snapshot (`data.senderHealth`) rather
        // than the health-check cache, so patch the summary's
        // senderHealth field too — otherwise the Dashboard pill would
        // keep showing the stale status until the next 30s poll. This
        // mirrors the cache-patching pattern used after a successful
        // send (see compose.tsx).
        const summaryKey = getGetDashboardSummaryQueryKey();
        const cachedSummary =
          queryClient.getQueryData<DashboardSummary>(summaryKey);
        if (cachedSummary) {
          queryClient.setQueryData<DashboardSummary>(summaryKey, {
            ...cachedSummary,
            senderHealth: fresh,
          });
        }

        // The server has just consumed this user's only token in the
        // 10-second window — disable the button (with countdown) until
        // the rate limit refills, so a quick double-click won't be
        // greeted with a 429.
        setCooldown({
          activeUntilMs: Date.now() + SENDER_RECHECK_COOLDOWN_MS,
          reason: "post_recheck",
        });

        if (fresh.status === "verified") {
          toast.success(`${fresh.address} is verified — sends will go through.`);
        } else if (fresh.status === "unverified") {
          toast.error(
            fresh.reason ??
              `${fresh.address} is still not verified in Resend.`,
            { duration: 10000 },
          );
        } else {
          toast.message(
            fresh.reason ??
              "Couldn't reach Resend to confirm verification — try again in a moment.",
          );
        }
      },
      onError: (err: unknown) => {
        if (apiErrorStatus(err) === 429) {
          const limit = apiErrorRateLimit(err);
          const resetMs = limit ? Date.parse(limit.resetAt) : NaN;
          if (Number.isFinite(resetMs) && resetMs > Date.now()) {
            // The parent banner renders an inline cooldown message
            // (see compose.tsx / brand.tsx) when reason === "rate_limit",
            // so don't double up with a toast on top of it.
            setCooldown({
              activeUntilMs: resetMs,
              reason: "rate_limit",
            });
            return;
          }
          // Fallback: server didn't give us a usable resetAt — fall back to
          // the toast so the user still gets feedback.
          toast.error(
            apiErrorMessage(
              err,
              "Recheck rate limit reached. Wait a few seconds before trying again.",
            ),
          );
        } else {
          toast.error(apiErrorMessage(err, "Failed to recheck sender."));
        }
      },
    },
  });

  const isPending = refreshMutation.isPending;
  const isDisabled = isPending || isCountingDown;

  let label: string;
  if (isPending) {
    label = "Rechecking…";
  } else if (isCountingDown) {
    label = `Recheck in ${secondsLeft}s…`;
  } else {
    label = "Recheck now";
  }

  const button = (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      onClick={() => refreshMutation.mutate()}
      disabled={isDisabled}
      data-testid={testId}
      aria-label="Recheck sender verification status now"
      aria-live={isCountingDown ? "polite" : undefined}
    >
      {isPending ? (
        <Loader2
          className={
            compactOnMobile
              ? "w-3.5 h-3.5 sm:mr-1.5 animate-spin"
              : "w-3.5 h-3.5 mr-1.5 animate-spin"
          }
        />
      ) : (
        <RotateCw
          className={
            compactOnMobile ? "w-3.5 h-3.5 sm:mr-1.5" : "w-3.5 h-3.5 mr-1.5"
          }
        />
      )}
      <span className={compactOnMobile ? "hidden sm:inline" : undefined}>
        {label}
      </span>
    </Button>
  );

  if (!isCountingDown) return button;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Wrap the disabled button in a span so the tooltip trigger still
            receives pointer events (disabled buttons swallow them). */}
        <span tabIndex={0} className="inline-block">
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        Resend lets us recheck once every {SENDER_RECHECK_COOLDOWN_SECONDS}{" "}
        seconds. Try again in {secondsLeft} second
        {secondsLeft === 1 ? "" : "s"}.
      </TooltipContent>
    </Tooltip>
  );
}
